import React, { useState, useEffect, useRef } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const APPROVAL_POLL_INTERVAL_MS = 45000;

const normaliseApprovalVersion = (value) => {
  const str = String(value || '').trim();
  if (!str) return '0.0';
  const clean = str.replace(/^v/i, '');
  const parts = clean.split('.');
  const major = parseInt(parts[0], 10) || 0;
  const minor = parts.length > 1 ? parseInt(parts[1], 10) || 0 : 0;
  return `${major}.${minor}`;
};

const STATUS_CONFIG = {
  NOT_SENT: { label: 'Not Sent', color: 'bg-slate-100 text-slate-600', icon: FiIcons.FiSend, border: 'border-slate-200' },
  PENDING: { label: 'Awaiting Approval', color: 'bg-amber-100 text-amber-700', icon: FiIcons.FiClock, border: 'border-amber-200' },
  APPROVED: { label: 'Approved', color: 'bg-green-100 text-green-700', icon: FiIcons.FiCheckCircle, border: 'border-green-200' },
  CHANGES_REQUESTED: { label: 'Changes Requested', color: 'bg-blue-100 text-blue-700', icon: FiIcons.FiEdit2, border: 'border-blue-200' },
  EXPIRED: { label: 'Expired', color: 'bg-orange-100 text-orange-700', icon: FiIcons.FiAlertTriangle, border: 'border-orange-200' },
  REVOKED: { label: 'Revoked', color: 'bg-red-100 text-red-700', icon: FiIcons.FiXCircle, border: 'border-red-200' },
  SEND_FAILED: { label: 'Send Failed', color: 'bg-red-100 text-red-700', icon: FiIcons.FiAlertCircle, border: 'border-red-200' },
};

const fmt = (d) => (d ? new Date(d).toLocaleString() : null);
const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString('en-AU', { year: 'numeric', month: 'short', day: 'numeric' })
    : null;

function DetailRow({ icon, label, children }) {
  if (!children) return null;
  return (
    <div className="flex items-start gap-2 py-1">
      <SafeIcon icon={icon} className="text-gray-400 text-sm mt-0.5 shrink-0" />
      <div className="min-w-0">
        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{label}</span>
        <p className="text-xs text-gray-700 font-medium break-words">{children}</p>
      </div>
    </div>
  );
}

function LinkButton({ href, icon, label, className }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-black border rounded-lg uppercase tracking-wider transition-all ${className}`}
    >
      <SafeIcon icon={icon} />
      {label}
    </a>
  );
}

function HistoryRow({ record }) {
  const cfg = STATUS_CONFIG[record.status] || STATUS_CONFIG.NOT_SENT;
  const docNum = record.documentNumber || record.proposalDocumentNumber || '—';
  const ver = record.documentVersion || record.version || '—';
  return (
    <div className="flex items-center gap-2 py-1.5 border-b border-gray-50 last:border-0">
      <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wider ${cfg.color}`}>{cfg.label}</span>
      <span className="text-[10px] text-gray-500 font-medium truncate flex-1 min-w-0">{docNum} · v{ver}</span>
      <span className="text-[9px] text-gray-400 font-medium whitespace-nowrap shrink-0">{fmtDate(record.sentAt) || '—'}</span>
      {record.decisionAt && (
        <span className="text-[9px] text-gray-400 font-medium whitespace-nowrap shrink-0">→ {fmtDate(record.decisionAt)}</span>
      )}
    </div>
  );
}

export default function ApprovalStatusCard({
  approval,
  isLoading,
  onRefresh,
  onResend,
  onRevoke,
  onDownloadApprovedPdf,
  approvedPdfInProgress,
  onEmailApprovedPdfToClient,
  approvedPdfEmailInProgress,
  approvedPdfEmailStage,
  approvalOverview,
  currentDocumentNumber,
  currentDocumentVersion,
  documentStatus,
  onRestoreApprovedVersion,
  restoreApprovalInProgress,
  onRecoverUnchangedApprovedVersion,
  recoverUnchangedInProgress,
  onDownloadHistoricalApprovedPdf,
  historicalPdfDownloadInProgress,
}) {
  const [showHistory, setShowHistory] = useState(false);
  const autoRefreshInFlightRef = useRef(false);
  const onRefreshRef = useRef(onRefresh);
  const isLoadingRef = useRef(isLoading);
  onRefreshRef.current = onRefresh;
  isLoadingRef.current = isLoading;

  const recipientName = approval?.recipientName || approval?.clientName || '';
  const recipientEmail = approval?.recipientEmail || approval?.clientEmail || '';
  const version = approval?.version || approval?.documentVersion || '';
  const approvalPdfUrl = approval?.approvalPdfUrl || approval?.pdfDriveUrl || '';
  const receiptUrl = approval?.receiptUrl || approval?.receiptDriveUrl || '';
  const driveFolderUrl = approval?.driveFolderUrl || approval?.requestFolderUrl || '';
  const documentNumber = approval?.documentNumber || approval?.proposalDocumentNumber || '';
  const clientComments = approval?.clientComments || '';
  const recipientDisplay =
    recipientName && recipientEmail ? `${recipientName} (${recipientEmail})` : recipientName || recipientEmail || null;
  const docRef = [documentNumber, version ? `v${version}` : ''].filter(Boolean).join(' ');

  const exactApproved = approvalOverview?.exactApproved || null;
  const latestApproved = approvalOverview?.latestApproved || null;
  const latestExactRequest = approvalOverview?.latestExactRequest || null;
  const history = approvalOverview?.history || [];

  const hasExactApproved =
    exactApproved &&
    exactApproved.status === 'APPROVED' &&
    exactApproved.documentNumber === currentDocumentNumber &&
    normaliseApprovalVersion(exactApproved.documentVersion || exactApproved.version) === normaliseApprovalVersion(currentDocumentVersion);

  const shouldAutoRefresh = approval?.status === 'PENDING' && !hasExactApproved;

  // Pending approvals update themselves while this SOW is open. Poll lightly,
  // refresh immediately when the tab becomes active again, and stop as soon as
  // a final/authoritative state is returned. Existing backend endpoints and
  // approval evidence remain unchanged.
  useEffect(() => {
    if (!shouldAutoRefresh) return undefined;

    const refreshIfVisible = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      if (isLoadingRef.current || autoRefreshInFlightRef.current) return;
      const refresh = onRefreshRef.current;
      if (typeof refresh !== 'function') return;

      autoRefreshInFlightRef.current = true;
      try {
        await refresh();
      } catch (err) {
        console.warn('ScopeShield: Automatic approval status refresh was deferred.', err);
      } finally {
        autoRefreshInFlightRef.current = false;
      }
    };

    const intervalId = window.setInterval(() => {
      void refreshIfVisible();
    }, APPROVAL_POLL_INTERVAL_MS);
    const handleFocus = () => void refreshIfVisible();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void refreshIfVisible();
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [shouldAutoRefresh, approval?.requestId]);

  const isDraft =
    documentStatus === 'Draft' || !documentStatus || (documentStatus !== 'Published' && documentStatus !== 'Sent');

  const showRestorePanel =
    hasExactApproved &&
    (isDraft || !approval || approval.status !== 'APPROVED' || (approval.status === 'APPROVED' && !approval.requestId));

  const showPreviousApprovedNotice =
    !hasExactApproved &&
    latestApproved &&
    normaliseApprovalVersion(latestApproved.documentVersion || latestApproved.version) !== normaliseApprovalVersion(currentDocumentVersion);

  const hasLaterAttempt =
    hasExactApproved &&
    latestExactRequest &&
    latestExactRequest.requestId !== exactApproved.requestId &&
    (latestExactRequest.status === 'SEND_FAILED' || latestExactRequest.status === 'PENDING');

  const anyRecoveryOrDownloadRunning =
    recoverUnchangedInProgress || historicalPdfDownloadInProgress || restoreApprovalInProgress;

  if ((!approval || approval.status === 'NOT_SENT') && !showRestorePanel) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
            <SafeIcon icon={isLoading ? FiIcons.FiRefreshCw : FiIcons.FiSend} className={`text-slate-400 ${isLoading ? 'animate-spin' : ''}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest">Client Approval</h3>
            <p className="text-xs text-gray-400 font-medium">{isLoading ? 'Checking approval status…' : 'Not sent for approval yet'}</p>
          </div>
          <button onClick={onRefresh} disabled={isLoading} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50 shrink-0" title="Refresh status">
            <SafeIcon icon={FiIcons.FiRefreshCw} className={`text-sm text-slate-400 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {showPreviousApprovedNotice && (
          <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-3.5">
            <h4 className="text-[10px] font-black text-blue-800 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
              <SafeIcon icon={FiIcons.FiInfo} className="text-xs" />
              Previously approved version
            </h4>
            <div className="text-[11px] text-blue-700 font-medium space-y-0.5 mb-3">
              <div>Approved SOW: {latestApproved.documentNumber || latestApproved.proposalDocumentNumber || '—'} v{latestApproved.documentVersion || latestApproved.version || '—'}</div>
              <div>Current SOW: {currentDocumentNumber || '—'} v{currentDocumentVersion || '—'}</div>
              <div>Approved: {fmtDate(latestApproved.decisionAt) || '—'}</div>
            </div>
            <p className="text-[10px] text-blue-600 font-medium mb-3 italic">
              The current SOW version is not covered by that approval. Publish and send the new revision for approval.
            </p>
            {onDownloadHistoricalApprovedPdf && (
              <button type="button" onClick={onDownloadHistoricalApprovedPdf} disabled={anyRecoveryOrDownloadRunning}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[10px] font-black text-white bg-blue-600 rounded-xl hover:bg-blue-700 uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none mb-2">
                <SafeIcon icon={historicalPdfDownloadInProgress ? FiIcons.FiRefreshCw : FiIcons.FiDownload} className={historicalPdfDownloadInProgress ? 'animate-spin' : ''} />
                {historicalPdfDownloadInProgress ? 'Downloading…' : 'Download Original Approved Copy'}
              </button>
            )}
            {onRecoverUnchangedApprovedVersion && (
              <>
                <button type="button" onClick={onRecoverUnchangedApprovedVersion} disabled={anyRecoveryOrDownloadRunning}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[10px] font-black text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-200 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-none mb-2">
                  <SafeIcon icon={recoverUnchangedInProgress ? FiIcons.FiRefreshCw : FiIcons.FiRotateCw} className={recoverUnchangedInProgress ? 'animate-spin' : ''} />
                  {recoverUnchangedInProgress ? 'Recovering…' : 'Recover Unchanged Approved Version'}
                </button>
                <p className="text-[9px] text-blue-600/80 font-medium italic leading-relaxed">
                  Use recovery only when no document content changed after the original approval. This action restores the original approved version and does not create a new approval.
                </p>
              </>
            )}
          </div>
        )}

        {history.length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-3">
            <button onClick={() => setShowHistory(!showHistory)} className="w-full flex items-center justify-between text-[10px] font-black text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors">
              <span>Approval history ({history.length})</span>
              <SafeIcon icon={showHistory ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} className="text-xs" />
            </button>
            {showHistory && (
              <div className="mt-2">
                {history.slice(0, 5).map((record, i) => (
                  <HistoryRow key={record.requestId || i} record={record} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  const cfg = STATUS_CONFIG[approval?.status] || STATUS_CONFIG.NOT_SENT;
  const canResend = ['PENDING', 'EXPIRED', 'SEND_FAILED'].includes(approval?.status) && !hasExactApproved;
  const canRevoke = approval?.status === 'PENDING' && !hasExactApproved;
  const isApproved = approval?.status === 'APPROVED' || hasExactApproved;
  const isPending = approval?.status === 'PENDING';
  const isSendFailed = approval?.status === 'SEND_FAILED';

  let emailitGuidance = '';
  if (isSendFailed && approval.sendErrorCode) {
    const code = approval.sendErrorCode;
    if (code === 'EMAILIT_HTTP_401' || code === 'EMAILIT_AUTHENTICATION_FAILED') {
      emailitGuidance = 'Check the Emailit API key and sending permission.';
    } else if (code === 'EMAILIT_HTTP_403' || code === 'EMAILIT_SENDER_NOT_VERIFIED') {
      emailitGuidance = 'Check Emailit workspace verification and the verified From domain.';
    } else if (code === 'EMAILIT_HTTP_429' || code === 'EMAILIT_RATE_LIMITED') {
      emailitGuidance = 'Emailit rate-limited the request. Wait briefly and use Resend.';
    } else if (code === 'EMAILIT_MESSAGE_TOO_LARGE') {
      emailitGuidance = 'Reduce the approval PDF or stored-logo file size.';
    } else {
      emailitGuidance = 'Run the Emailit Delivery Diagnostic from the ScopeShield Sheet menu.';
    }
  }

  const displayApproval = hasExactApproved ? exactApproved : approval;
  const displayRecipientName = displayApproval?.recipientName || displayApproval?.clientName || '';
  const displayRecipientEmail = displayApproval?.recipientEmail || displayApproval?.clientEmail || '';
  const displayDocNumber = displayApproval?.documentNumber || displayApproval?.proposalDocumentNumber || '';
  const displayVersion = displayApproval?.documentVersion || displayApproval?.version || '';

  const approvedPdfEmailSentAt = displayApproval?.approvedPdfEmailSentAt || null;
  const approvedPdfEmailProvider = displayApproval?.approvedPdfEmailProvider || null;

  const emailProviderLabel = () => {
    if (!approvedPdfEmailProvider) return null;
    const p = String(approvedPdfEmailProvider).toUpperCase();
    if (p.includes('EMAILIT')) return 'Sent via Emailit';
    if (p.includes('GMAIL') || p.includes('GOOGLE')) return 'Sent via Google Mail backup';
    return null;
  };

  return (
    <div className={`bg-white rounded-2xl border ${cfg.border} shadow-sm overflow-visible`}>
      <div className={`${cfg.color} px-5 py-3 flex items-center gap-3 rounded-t-[15px]`}>
        <SafeIcon icon={cfg.icon} className="text-base shrink-0" />
        <h3 className="text-xs font-black uppercase tracking-widest flex-1 min-w-0">
          Client Approval — {hasExactApproved ? 'Approved' : cfg.label}
        </h3>
        <button onClick={onRefresh} disabled={isLoading} className="p-1.5 rounded-lg hover:bg-white/30 transition-colors disabled:opacity-50 shrink-0" title="Refresh status">
          <SafeIcon icon={FiIcons.FiRefreshCw} className={`text-sm ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="p-5 space-y-0.5">
        <DetailRow icon={FiIcons.FiUser} label="Recipient">
          {displayRecipientName && displayRecipientEmail ? `${displayRecipientName} (${displayRecipientEmail})` : displayRecipientName || displayRecipientEmail || recipientDisplay}
        </DetailRow>
        <DetailRow icon={FiIcons.FiSend} label="Sent">{fmt(displayApproval?.sentAt || approval?.sentAt)}</DetailRow>
        <DetailRow icon={FiIcons.FiClock} label="Expires">{fmt(approval?.expiresAt)}</DetailRow>
        <DetailRow icon={FiIcons.FiCheckCircle} label="Approval Date">{fmt(displayApproval?.decisionAt || approval?.decisionAt)}</DetailRow>
        <DetailRow icon={FiIcons.FiHash} label="Document">
          {[displayDocNumber || documentNumber, (displayVersion || version) ? `v${displayVersion || version}` : ''].filter(Boolean).join(' ') || docRef || null}
        </DetailRow>
        <DetailRow icon={FiIcons.FiMessageSquare} label="Client Comments">{clientComments}</DetailRow>

        {isPending && approval.emailSendStatus === 'ACCEPTED' && approval.sentAt && (
          <div className="pt-2">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiCheck} className="text-emerald-500 text-xs shrink-0" />
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                Email accepted by Emailit{' '}
                {approval.emailitProviderStatus && ` • ${approval.emailitProviderStatus}${approval.emailSendDurationMs ? ` • ${(approval.emailSendDurationMs / 1000).toFixed(1)}s` : ''}`}
              </p>
            </div>
          </div>
        )}

        {hasLaterAttempt && (
          <div className="pt-2">
            <div className="flex items-start gap-2 bg-gray-50 border border-gray-100 rounded-lg p-2.5">
              <SafeIcon icon={FiIcons.FiInfo} className="text-gray-400 text-xs mt-0.5 shrink-0" />
              <p className="text-[10px] text-gray-500 font-medium leading-relaxed">
                A later {latestExactRequest.status === 'SEND_FAILED' ? 'failed' : 'pending'} approval attempt exists for this version. The original approval above remains authoritative.
              </p>
            </div>
          </div>
        )}

        {showRestorePanel && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4">
            <h4 className="text-xs font-black text-green-800 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
              <SafeIcon icon={FiIcons.FiShield} className="text-sm" />
              Secure approval found
            </h4>
            <p className="text-xs text-green-700 font-medium leading-relaxed mb-3">
              ScopeShield found the original approval for this exact SOW number and version.
            </p>
            <div className="bg-white/60 rounded-lg p-3 border border-green-100 space-y-1 mb-3">
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold text-green-800/60 uppercase tracking-widest">Approved on</span>
                <span className="text-[10px] font-bold text-green-800">{fmtDate(exactApproved.decisionAt) || '—'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold text-green-800/60 uppercase tracking-widest">Approved version</span>
                <span className="text-[10px] font-bold text-green-800">{exactApproved.documentNumber || exactApproved.proposalDocumentNumber || '—'} · v{exactApproved.documentVersion || exactApproved.version || '—'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[10px] font-bold text-green-800/60 uppercase tracking-widest">Recipient</span>
                <span className="text-[10px] font-bold text-green-800 truncate ml-2">{exactApproved.recipientName || exactApproved.clientName || '—'}</span>
              </div>
            </div>
            <button onClick={onRestoreApprovedVersion} disabled={restoreApprovalInProgress}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-black text-white bg-green-600 rounded-xl hover:bg-green-700 uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-200">
              <SafeIcon icon={restoreApprovalInProgress ? FiIcons.FiRefreshCw : FiIcons.FiRotateCw} className={restoreApprovalInProgress ? 'animate-spin' : ''} />
              {restoreApprovalInProgress ? 'Restoring…' : 'Restore Published + Approved'}
            </button>
          </div>
        )}

        {showPreviousApprovedNotice && (
          <div className="mt-4 bg-blue-50 border border-blue-100 rounded-xl p-3.5">
            <h4 className="text-[10px] font-black text-blue-800 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
              <SafeIcon icon={FiIcons.FiInfo} className="text-xs" />
              Previously approved version
            </h4>
            <div className="text-[11px] text-blue-700 font-medium space-y-0.5 mb-3">
              <div>Approved SOW: {latestApproved.documentNumber || latestApproved.proposalDocumentNumber || '—'} v{latestApproved.documentVersion || latestApproved.version || '—'}</div>
              <div>Current SOW: {currentDocumentNumber || '—'} v{currentDocumentVersion || '—'}</div>
              <div>Approved: {fmtDate(latestApproved.decisionAt) || '—'}</div>
            </div>
            <p className="text-[10px] text-blue-600 font-medium mb-3 italic">
              The current SOW version is not covered by that approval. Publish and send the new revision for approval.
            </p>
            {onDownloadHistoricalApprovedPdf && (
              <button type="button" onClick={onDownloadHistoricalApprovedPdf} disabled={anyRecoveryOrDownloadRunning}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[10px] font-black text-white bg-blue-600 rounded-xl hover:bg-blue-700 uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none mb-2">
                <SafeIcon icon={historicalPdfDownloadInProgress ? FiIcons.FiRefreshCw : FiIcons.FiDownload} className={historicalPdfDownloadInProgress ? 'animate-spin' : ''} />
                {historicalPdfDownloadInProgress ? 'Downloading…' : 'Download Original Approved Copy'}
              </button>
            )}
            {onRecoverUnchangedApprovedVersion && (
              <>
                <button type="button" onClick={onRecoverUnchangedApprovedVersion} disabled={anyRecoveryOrDownloadRunning}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[10px] font-black text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-200 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-none mb-2">
                  <SafeIcon icon={recoverUnchangedInProgress ? FiIcons.FiRefreshCw : FiIcons.FiRotateCw} className={recoverUnchangedInProgress ? 'animate-spin' : ''} />
                  {recoverUnchangedInProgress ? 'Recovering…' : 'Recover Unchanged Approved Version'}
                </button>
                <p className="text-[9px] text-blue-600/80 font-medium italic leading-relaxed">
                  Use recovery only when no document content changed after the original approval. This action restores the original approved version and does not create a new approval.
                </p>
              </>
            )}
          </div>
        )}

        {isApproved && onDownloadApprovedPdf && !showRestorePanel && (
          <div className="pt-3">
            <button type="button" onClick={onDownloadApprovedPdf} disabled={approvedPdfInProgress || approvedPdfEmailInProgress}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 text-xs font-black text-white bg-green-600 rounded-xl hover:bg-green-700 uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-200 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none">
              <SafeIcon icon={approvedPdfInProgress ? FiIcons.FiRefreshCw : FiIcons.FiDownloadCloud} className={approvedPdfInProgress ? 'animate-spin' : ''} />
              {approvedPdfInProgress ? 'Generating Approved PDF…' : 'Download Approved PDF'}
            </button>
          </div>
        )}

        {isApproved && onEmailApprovedPdfToClient && !showRestorePanel && (
          <div className="pt-2">
            <button type="button" onClick={onEmailApprovedPdfToClient} disabled={approvedPdfEmailInProgress || approvedPdfInProgress}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[10px] font-black text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-200 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-none">
              <SafeIcon icon={approvedPdfEmailInProgress ? FiIcons.FiRefreshCw : FiIcons.FiMail} className={approvedPdfEmailInProgress ? 'animate-spin' : ''} />
              {approvedPdfEmailInProgress ? (approvedPdfEmailStage || 'Preparing and emailing…') : 'Email Approved PDF'}
            </button>
          </div>
        )}

        {isApproved && onDownloadHistoricalApprovedPdf && !showRestorePanel && (
          <div className="pt-2">
            <button type="button" onClick={onDownloadHistoricalApprovedPdf} disabled={anyRecoveryOrDownloadRunning || approvedPdfEmailInProgress}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-[10px] font-black text-white bg-blue-600 rounded-xl hover:bg-blue-700 uppercase tracking-widest transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-blue-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none">
              <SafeIcon icon={historicalPdfDownloadInProgress ? FiIcons.FiRefreshCw : FiIcons.FiDownload} className={historicalPdfDownloadInProgress ? 'animate-spin' : ''} />
              {historicalPdfDownloadInProgress ? 'Downloading…' : 'Original Approved Copy'}
            </button>
          </div>
        )}

        {((isPending && approvalPdfUrl) || (isApproved && (receiptUrl || driveFolderUrl))) && !showRestorePanel && (
          <div className="flex flex-wrap gap-2 pt-2">
            {isPending && approvalPdfUrl && (
              <LinkButton href={approvalPdfUrl} icon={FiIcons.FiFileText} label="Original Approval Copy" className="text-indigo-600 bg-indigo-50 border-indigo-100 hover:bg-indigo-100" />
            )}
            {isApproved && receiptUrl && (
              <LinkButton href={receiptUrl} icon={FiIcons.FiFile} label="Receipt" className="text-emerald-600 bg-emerald-50 border-emerald-100 hover:bg-emerald-100" />
            )}
            {isApproved && driveFolderUrl && (
              <LinkButton href={driveFolderUrl} icon={FiIcons.FiFolder} label="Drive Folder" className="text-slate-600 bg-slate-50 border-slate-200 hover:bg-slate-100" />
            )}
          </div>
        )}

        {isApproved && !showRestorePanel && approvedPdfEmailSentAt && (
          <div className="pt-2">
            <p className="text-[9px] text-gray-400 font-medium leading-relaxed">
              Approved PDF emailed on {fmtDate(approvedPdfEmailSentAt)}
              {emailProviderLabel() && ` · ${emailProviderLabel()}`}
            </p>
          </div>
        )}

        {isApproved && !showRestorePanel && (
          <div className="mt-2 bg-gray-50 border border-gray-100 rounded-lg p-2.5">
            <h4 className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1">Approved and locked</h4>
            <p className="text-[10px] text-gray-500 font-medium leading-relaxed">
              Download the Approved PDF for the complete SOW with visible approval confirmation. Reopen as Draft only when a new version is required.
            </p>
          </div>
        )}

        {isSendFailed && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4">
            <h4 className="text-xs font-black text-red-800 uppercase tracking-widest mb-1.5 flex items-center gap-1.5">
              <SafeIcon icon={FiIcons.FiAlertCircle} className="text-sm" />
              Email delivery failed
            </h4>
            <p className="text-xs text-red-700 font-medium leading-relaxed mb-3">
              {approval.sendErrorMessage || 'The approval record and PDF were created, but Emailit did not accept the email request. Run the Emailit Delivery Diagnostic in the bound Google Sheet, then resend this request.'}
            </p>
            {(approval.sendErrorCode || approval.deliveryAttemptCount || approval.sendErrorAt) && (
              <div className="bg-white/60 rounded-lg p-3 border border-red-100 space-y-1.5 mb-3">
                {approval.sendErrorCode && (
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold text-red-800/60 uppercase tracking-widest">Error code</span>
                    <span className="text-[10px] font-mono font-bold text-red-800">{approval.sendErrorCode}</span>
                  </div>
                )}
                {approval.deliveryAttemptCount > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold text-red-800/60 uppercase tracking-widest">Attempt</span>
                    <span className="text-[10px] font-bold text-red-800">{approval.deliveryAttemptCount}</span>
                  </div>
                )}
                {approval.sendErrorAt && (
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-bold text-red-800/60 uppercase tracking-widest">Failed at</span>
                    <span className="text-[10px] font-bold text-red-800">{fmt(approval.sendErrorAt)}</span>
                  </div>
                )}
              </div>
            )}
            {emailitGuidance && <p className="text-[11px] text-red-800 font-medium italic">{emailitGuidance}</p>}
          </div>
        )}

        {(canResend || canRevoke) && (
          <div className="flex gap-2 pt-3 border-t border-gray-100 mt-2">
            {canResend && (
              <button onClick={onResend} disabled={isLoading}
                className="flex items-center gap-1.5 px-4 py-2 text-[10px] font-black text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 uppercase tracking-widest transition-all disabled:opacity-50">
                <SafeIcon icon={FiIcons.FiRefreshCw} className={isLoading ? 'animate-spin' : ''} />
                Resend
              </button>
            )}
            {canRevoke && (
              <button onClick={onRevoke} disabled={isLoading}
                className="flex items-center gap-1.5 px-4 py-2 text-[10px] font-black text-red-700 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 uppercase tracking-widest transition-all disabled:opacity-50">
                <SafeIcon icon={FiIcons.FiXCircle} />
                Revoke
              </button>
            )}
          </div>
        )}

        {history.length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-3">
            <button onClick={() => setShowHistory(!showHistory)} className="w-full flex items-center justify-between text-[10px] font-black text-gray-400 uppercase tracking-widest hover:text-gray-600 transition-colors">
              <span>Approval history ({history.length})</span>
              <SafeIcon icon={showHistory ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} className="text-xs" />
            </button>
            {showHistory && (
              <div className="mt-2">
                {history.slice(0, 5).map((record, i) => (
                  <HistoryRow key={record.requestId || i} record={record} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}